import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, serverTimestamp, query, where, onSnapshot } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL, sendEmailNotification } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Truck, User, MapPin, Clock, Paperclip, Printer, CheckCircle, Loader2, FileCheck, FileText } from 'lucide-react';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import DCPreviewContent from './DCPreviewContent.js';
import { useConfig } from './hooks/useConfig.js';
import { getCleanStorageFileName } from './utils/storageFileName.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import AttachmentCard from './components/AttachmentCard.js';
import { isAdminUser } from './accessControl.js';
import StatusBadge from './components/StatusBadge.js';
import { updateDispatchAudit } from './utils/dispatchAudit.js';
import { updateOrderWithAudit } from './utils/orderAudit.js';
import { formatVehicleNumber } from './utils/vehicleNumber.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
};

const getDefaultDcRemarks = (typeOfMOC) => {
  if (typeOfMOC === 'Rental') {
    return 'GOODS NOT FOR SALE SENT ON HIRING RETURNABLE BASIS ';
  }

  if (typeOfMOC === 'Interbranch Transfer') {
    return 'GOODS NOT FOR SALE SENT ON INTERBRAHC TRANSFER ONLY';
  }

  return 'GOODS FOR SALE BASIS';
};

const isAllowedPodFile = (file) => {
  if (!file) return false;
  return (
    ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) ||
    /\.(pdf|jpe?g|png)$/i.test(file.name || '')
  );
};

const InTransitDetail = ({ db, user }) => {
  const { config } = useConfig();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [sourceWarehouse, setSourceWarehouse] = useState(null);
  const [destinationWarehouse, setDestinationWarehouse] = useState(null);
  const [dcPreviewData, setDcPreviewData] = useState(null);
  const printRef = useRef();

  // Delivery State
  const [isDelivering, setIsDelivering] = useState(location.state?.mode === 'deliver');
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState({ pod: false });
  const [deliveryData, setDeliveryData] = useState({
    deliveryDate: new Date().toISOString().split('T')[0],
    receiverName: '',
    receiverContact: '',
    podUrl: '',
    deliveryConfirmedBy: '',
    transportCharges: '',
    declarationChecked: false
  });
  const fieldStaffOptions = config?.field_staff?.length ? config.field_staff : (config?.delivery_confirmed_by || []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const fetchedFirms = firmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFirms(fetchedFirms);

        const docRef = doc(db, "dispatches", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mData = docSnap.data();
          setFormData({
            id: docSnap.id,
            ...mData,
            vehicleNumber: formatVehicleNumber(mData.vehicleNumber || '')
          });

          // Reconstruct dcPreviewData similar to DCDetail.js
          const orderSnap = mData.orderId ? await getDoc(doc(db, "orders", mData.orderId)) : null;
          const orderData = orderSnap?.exists() ? { id: orderSnap.id, ...orderSnap.data() } : null;
          setOrder(orderData);

          const customerData = mData.customerID ? (await getDoc(doc(db, "customers", mData.customerID))).data() : null;
          let sourceWarehouseData = null;
          if (mData.typeOfMOC === 'Interbranch Transfer' && mData.sourceWarehouseID) {
            const swSnap = await getDoc(doc(db, "warehouses", mData.sourceWarehouseID));
            if (swSnap.exists()) {
              sourceWarehouseData = swSnap.data();
            }
          }
          setSourceWarehouse(sourceWarehouseData ? { id: mData.sourceWarehouseID, ...sourceWarehouseData } : null);

          let destinationWarehouseData = null;
          if (mData.typeOfMOC === 'Interbranch Transfer') {
            const destinationWarehouseName = mData.destinationWarehouseName || mData.customerName || mData.shipToCustomer || '';
            if (destinationWarehouseName) {
              const destSnap = await getDocs(
                query(collection(db, "warehouses"), where("name", "==", destinationWarehouseName))
              );
              if (destSnap.docs.length > 0) {
                destinationWarehouseData = { id: destSnap.docs[0].id, ...destSnap.docs[0].data() };
              }
            }
          }
          setDestinationWarehouse(destinationWarehouseData);

          const isInterbranch = mData.typeOfMOC === 'Interbranch Transfer';
          const salesShippingSnapshot = !isInterbranch
            ? getSalesShippingSnapshot(mData, orderData)
            : null;
          const selectedFirmId = mData.firmId || orderData?.firmId || '';
          const currentFirm = fetchedFirms.find((firm) => firm.id === selectedFirmId);

          const reconstructedComponents = (mData.components || []).map(mc => {
            let originalBaseQty = mc.perSet;
            if (!originalBaseQty && orderData) {
              orderData.boms?.forEach(bom => {
                const match = bom.components?.find(bc => bc.sku === mc.sku);
                if (match) originalBaseQty = match.baseQty;
              });
            }
            return { ...mc, perSet: originalBaseQty || 1, newQty: mc.newQty || 0, rentalQty: mc.rentalQty || 0 };
          });

          const newDcPreviewData = {
            firmId: selectedFirmId,
            challanNo: mData.challanNo || `DC-${mData.mocNo}`,
            dispatchDate: mData.dispatchDate || mData.deliveryDate || new Date().toISOString().split('T')[0],
            deliveryAgent: mData.deliveryAgent || '',
            deliveryMode: mData.deliveryMode || 'By Road',
            coordinator: mData.coordinator || mData.preparedBy || '',
            dispatchedThrough: mData.dispatchedThrough || '',
            poNumber: mData.poNumber || '',
            poDate: mData.poDate || '',
            irnNo: mData.irnNo || '',
            billToCustomer: mData.billToCustomer || (isInterbranch ? mData.warehouse : (orderData?.customerName || customerData?.company_name || mData.customerName || '')),
            shipToCustomer: isInterbranch
              ? (mData.shipToCustomer || mData.destinationContactName || mData.shippingPersonName || '')
              : (salesShippingSnapshot?.customerName || mData.shipToCustomer || mData.customerName || ''),
            billToAddress: mData.billToAddress || (isInterbranch ? formatAddress(sourceWarehouseData) : formatAddress(orderData?.billingAddress)),
            shipToAddress: isInterbranch
              ? (mData.shipToAddress || mData.shippingAddress || '')
              : getShippingSnapshotAddress(salesShippingSnapshot, mData.shipToAddress || mData.shippingAddress || ''),
            billToGstin: mData.billToGstin || (isInterbranch ? (currentFirm?.gstin || '') : (customerData?.gstin || customerData?.gst_number || '')),
            salesperson: mData.salesperson || orderData?.salesPerson || mData.approvedBySalesperson || '',
            orderBy: mData.orderBy || (isInterbranch ? sourceWarehouseData?.contactPerson : (orderData?.billingAddress?.attention || '')),
            orderByContact: mData.orderByContact || (isInterbranch ? sourceWarehouseData?.phone : (orderData?.billingAddress?.phone || '')),
            sitePerson: isInterbranch
              ? (mData.sitePerson || mData.destinationContactName || mData.shippingPersonName || '')
              : (salesShippingSnapshot?.contactName || mData.sitePerson || orderData?.shippingAddress?.attention || ''),
            sitePersonContact: isInterbranch
              ? (mData.sitePersonContact || mData.destinationContactPhone || mData.shippingPersonPhone || '')
              : (salesShippingSnapshot?.contactPhone || mData.sitePersonContact || orderData?.shippingAddress?.phone || ''),
            billToEmail: mData.billToEmail || (isInterbranch ? sourceWarehouseData?.email : (orderData?.billingAddress?.email || '')),
            shipToEmail: isInterbranch
              ? (mData.shipToEmail || mData.destinationContactEmail || mData.shippingPersonEmail || '')
              : (salesShippingSnapshot?.contactEmail || mData.shipToEmail || orderData?.shippingAddress?.email || ''),
            itemCode: mData.itemCode || mData.materialCode || '',
            towerHeight: mData.towerHeight || mData.productSummary || '',
            totalMeter: mData.totalMeter || mData.totalMeterNumber || '',
            receivedByName: mData.receivedByName || '',
            receivedByContact: mData.receivedByContact || '',
            handoverDateTime: mData.handoverDateTime || '',
            vehicleNumber: formatVehicleNumber(mData.vehicleNumber || ''),
            vehicleType: mData.vehicleType || '',
            projectName: mData.projectName || orderData?.projectName || '',
            projectLocation: mData.projectLocation || orderData?.projectLocation?.address || orderData?.projectLocation || '',
            dcRemarks: mData.dcRemarks || getDefaultDcRemarks(mData.typeOfMOC),
            taxInvoiceUrl: mData.taxInvoiceUrl || '',
            ewayBillUrl: mData.ewayBillUrl || '',
            einvoiceUrl: mData.einvoiceUrl || '',
            isDCConfirmed: mData.isDCConfirmed || false,
            approvedByFinance: mData.approvedByFinance || '',
            components: reconstructedComponents
          };
          setDcPreviewData(newDcPreviewData);
          setSelectedFirm(currentFirm);

          const existingDeliveryDetails = mData.deliveryDetails || {};

          // Auto-fill delivery receiver details but keep them editable
          setDeliveryData(prev => ({
            ...prev,
            deliveryDate: existingDeliveryDetails.deliveryDate || prev.deliveryDate,
            receiverName: existingDeliveryDetails.receiverName || newDcPreviewData.sitePerson || '',
            receiverContact: existingDeliveryDetails.receiverContact || newDcPreviewData.sitePersonContact || '',
            podUrl: existingDeliveryDetails.podUrl || '',
            deliveryConfirmedBy: existingDeliveryDetails.deliveryConfirmedBy || '',
            transportCharges: existingDeliveryDetails.transportCharges || '',
            declarationChecked: Boolean(existingDeliveryDetails.declarationChecked)
          }));
        } else {
          alert("Dispatch not found");
          navigate('/ops/in-transit');
        }
      } catch (error) {
        console.error("Error fetching In Transit Dispatch:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!formData?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", formData.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, formData?.orderId]);

  const handleDeliveryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeliveryData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDocUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (type === 'pod' && !isAllowedPodFile(file)) {
      alert("Please upload the POD as a PDF, JPG, or PNG file.");
      e.target.value = '';
      return;
    }
    setUploadingDoc(prev => ({ ...prev, [type]: true }));
    try {
      const path = `delivery_docs/${id}/${type}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setDeliveryData(prev => ({ ...prev, [`${type}Url`]: url }));
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploadingDoc(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryData.receiverName || !deliveryData.receiverContact || !deliveryData.deliveryConfirmedBy || !deliveryData.declarationChecked || !deliveryData.podUrl) {
        alert("Please fill all mandatory fields (Receiver details, Confirming Person, POD upload, and Check Declaration).");
        return;
    }

    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      const auditTimestamp = new Date().toISOString();
      const updatedFormData = {
        ...formData,
        dispatchStatus: 'delivered',
        deliveryDetails: { ...deliveryData }
      };
      
      // Update Dispatch Document
      await updateDoc(doc(db, "dispatches", id), {
        dispatchStatus: 'delivered',
        deliveredAt: timestamp,
        deliveryDetails: { ...deliveryData },
        updatedAt: timestamp
      });
      await updateDispatchAudit(db, {
        dispatchId: id,
        dispatchData: {
          ...formData,
          dispatchStatus: 'delivered',
          orderId: formData.orderId || '',
          orderNumber: formData.orderNumber || '',
          mocNo: formData.mocNo || ''
        },
        user,
        historyEvent: {
          action: 'delivery_confirmed',
          dispatchStatus: 'delivered',
          timestamp: auditTimestamp,
          remarks: `Receiver: ${deliveryData.receiverName}`
        }
      });

      // Update associated Order Document if it exists
      if (formData.orderId) {
        await updateOrderWithAudit(db, {
          orderId: formData.orderId,
          orderPayload: {
            status: 'delivered',
            deliveryDetails: { ...deliveryData },
            updatedAt: auditTimestamp,
            updatedBy: user?.displayName || user?.email || 'Unknown',
            updatedByEmail: user?.email || ''
          },
          previousData: order || {},
          user,
          historyEvent: {
            action: 'delivery_confirmed',
            status: 'delivered',
            user,
            timestamp: auditTimestamp,
            remarks: `Receiver: ${deliveryData.receiverName}`
          }
        });
      }

      const emailQueued = await sendEmailNotification(db, 'delivered', updatedFormData, order);
      if (!emailQueued) {
        console.warn(`Delivery email was not queued for ${formData.mocNo}.`);
      }

      alert("Delivery Confirmed Successfully!");
      navigate('/ops/in-transit'); // Or navigate to a Delivered List if it exists
    } catch (error) {
      alert("Error confirming delivery: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };


  if (loading) return <Spinner />;
  if (!formData) return null; // Should not happen if loading is false and data is fetched or redirected

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getPodUploadValue = () => {
    if (!deliveryData.podUrl) return [];
    return [{
      name: getCleanStorageFileName(deliveryData.podUrl) || 'pod-file',
      url: deliveryData.podUrl
    }];
  };

  const handlePodFilesChange = async (files) => {
    if (!files?.length) {
      setDeliveryData(prev => ({ ...prev, podUrl: '' }));
      return;
    }

    const [file] = files;
    if (file?.url && !file?.size) {
      setDeliveryData(prev => ({ ...prev, podUrl: file.url }));
      return;
    }

    if (!isAllowedPodFile(file)) {
      alert("Please upload the POD as a PDF, JPG, or PNG file.");
      return;
    }

    setUploadingDoc(prev => ({ ...prev, pod: true }));
    try {
      const path = `delivery_docs/${id}/pod_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setDeliveryData(prev => ({ ...prev, podUrl: url }));
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploadingDoc(prev => ({ ...prev, pod: false }));
    }
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/ops/in-transit')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>In Transit: {formData.mocNo}</h2>
          {formData.dispatchStatus === 'dispatched' && (
            <StatusBadge status="dispatched" />
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {!isDelivering && !isAdminUser(user) && (
                <button onClick={() => setIsDelivering(true)} style={confirmBtnStyle}>
                    <CheckCircle size={18} /> Confirm Delivery
                </button>
            )}
            <button onClick={() => {
                const printContent = printRef.current.innerHTML;
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>DC ${dcPreviewData?.challanNo}</title>
                      <style>
                        body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
                        td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
                        @page { margin: 1cm; }
                      </style>
                    </head>
                    <body>${printContent}</body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
            }} style={secondaryBtnStyle}><Printer size={18} /> Export PDF</button>
        </div>
      </div>

      {isDelivering && (
        <div style={{ ...cardStyle, border: '2px solid #10b981', marginBottom: '20px', animation: 'fadeIn 0.3s' }}>
            <h3 style={{ ...sectionHeaderStyle, color: '#10b981' }}><CheckCircle size={20} /> Complete Delivery</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>Date of Delivery</label>
                    <input type="date" name="deliveryDate" value={deliveryData.deliveryDate} onChange={handleDeliveryChange} style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>{formData.typeOfMOC === 'Interbranch Transfer' ? 'Transfer No.' : 'Order No.'}</label>
                    <input type="text" value={formData.typeOfMOC === 'Interbranch Transfer' ? (formData.poNumber || '') : (formData.orderNumber || '')} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>DC No.</label>
                    <input type="text" value={formData.challanNo || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Customer Name</label>
                    <input type="text" value={formData.customerName || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                 <div>
                    <label style={labelStyle}>Billing Email</label>
                    <input type="text" value={dcPreviewData?.billToEmail || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Shipping Email</label>
                    <input type="text" value={dcPreviewData?.shipToEmail || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Receiver Name *</label>
                    <input type="text" name="receiverName" value={deliveryData.receiverName} onChange={handleDeliveryChange} style={inputStyle} required />
                </div>
                <div>
                    <label style={labelStyle}>Receiver Contact *</label>
                    <input type="text" name="receiverContact" value={deliveryData.receiverContact} onChange={handleDeliveryChange} style={inputStyle} required />
                </div>
                 <div>
                    <label style={labelStyle}>Delivery Confirmed By *</label>
                    <select name="deliveryConfirmedBy" value={deliveryData.deliveryConfirmedBy} onChange={handleDeliveryChange} style={inputStyle} required>
                        <option value="">Select Name...</option>
                        {fieldStaffOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Paid Transport Charges</label>
                    <input
                      type="number"
                      name="transportCharges"
                      value={deliveryData.transportCharges}
                      onChange={handleDeliveryChange}
                      style={inputStyle}
                      min="0"
                      step="0.01"
                      placeholder="Enter transport charges"
                    />
                </div>
                <div>
                    <label style={labelStyle}>Claimed Transport Charges(as per PO/order)</label>
                    <input
                      type="number"
                      value={order?.claimedChargesAsPerPoOrder ?? ''}
                      readOnly
                      style={{ ...inputStyle, backgroundColor: '#f8fafc', color: '#64748b' }}
                    />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>Upload POD (Signed DC) *</label>
                    <MultiFileUpload
                      value={getPodUploadValue()}
                      onFilesChange={handlePodFilesChange}
                      multiple={false}
                      maxFiles={1}
                      maxSizeMB={25}
                      allowedTypes={['application/pdf', 'image/jpeg', 'image/png']}
                      helperText="Upload or replace the signed POD in PDF, JPG, or PNG format."
                      uploading={uploadingDoc.pod}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                    <input type="checkbox" name="declarationChecked" checked={deliveryData.declarationChecked} onChange={handleDeliveryChange} style={{ width: '18px', height: '18px' }} required />
                    I have cross checked and confirmed with clients for material delivery.
                </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setIsDelivering(false)} style={secondaryBtnStyle}>Cancel</button>
                <button onClick={handleConfirmDelivery} disabled={isSaving} style={confirmBtnStyle}>
                    {isSaving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                    Submit Delivery
                </button>
            </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Driver & Vehicle Info */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><Truck size={18} /> Driver & Vehicle Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Vehicle Number</label>
              <p style={valueStyle}>{formatVehicleNumber(formData.vehicleNumber) || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Vehicle Type</label>
              <p style={valueStyle}>{formData.vehicleType || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Driver Name</label>
              <p style={valueStyle}>{formData.driverName || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Driver Contact</label>
              <p style={valueStyle}>{formData.driverContact || '---'}</p>
            </div>
          </div>
        </div>

        {/* Shipping Details */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><MapPin size={18} /> Shipping Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Customer Name</label>
              <p style={valueStyle}>{formData.customerName || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Shipping Address</label>
              <p style={valueStyle}>{formData.shippingAddress || '---'}</p>
            </div>
          </div>
        </div>

        {/* Dispatch Timings */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><Clock size={18} /> Dispatch Timings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Vehicle IN Time</label>
              <p style={valueStyle}>{formData.vehicleInTime || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Vehicle OUT Time (Dispatch)</label>
              <p style={valueStyle}>{formData.vehicleOutTime || '---'} on {formatDate(formData.vehicleDate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Render DC Preview from DCDetail logic */}
      <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
        <h3 style={{...sectionHeaderStyle, marginBottom: '20px'}}><FileText size={18} /> Delivery Challan (Document)</h3>
        {dcPreviewData ? (
          <DCPreviewContent
            dcData={dcPreviewData}
            linkedOrder={order}
            selectedFirm={selectedFirm}
            sourceWarehouse={sourceWarehouse}
            destinationWarehouse={destinationWarehouse}
            printRef={printRef}
            challanNo={dcPreviewData.challanNo}
            typeOfMOC={formData.typeOfMOC}
          />
        ) : <Spinner />}
      </div>

      {/* Document Attachments */}
      <div style={{...cardStyle, marginTop: '20px', animation: 'fadeIn 0.3s'}}>
        <h3 style={{...sectionHeaderStyle, marginBottom: '20px'}}>
          <Paperclip size={20} color="#2563eb"/> Document Attachments
        </h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px'}}>
          {formData.taxInvoiceUrl && (
            <AttachmentCard
              href={formData.taxInvoiceUrl}
              label="Tax Invoice"
              name={getCleanStorageFileName(formData.taxInvoiceUrl)}
              iconBackgroundColor="#c7d2fe"
              iconColor="#4f46e5"
            />
          )}
          {formData.ewayBillUrl && (
            <AttachmentCard
              href={formData.ewayBillUrl}
              label="E-way Bill"
              name={getCleanStorageFileName(formData.ewayBillUrl)}
              iconBackgroundColor="#dcfce7"
              iconColor="#166534"
            />
          )}
          {formData.einvoiceUrl && (
            <AttachmentCard
              href={formData.einvoiceUrl}
              label="E-Invoice"
              name={getCleanStorageFileName(formData.einvoiceUrl)}
              iconBackgroundColor="#fef9c3"
              iconColor="#854d0e"
            />
          )}
          {formData.vehicleProofUrl && (
            <AttachmentCard
              href={formData.vehicleProofUrl}
              label="Vehicle Proof"
              name={getCleanStorageFileName(formData.vehicleProofUrl)}
              icon={Truck}
              iconBackgroundColor="#e0f2fe"
              iconColor="#0284c7"
            />
          )}
          {formData.deliveryChallanUrl && (
            <AttachmentCard
              href={formData.deliveryChallanUrl}
              label="Delivery Challan"
              name={getCleanStorageFileName(formData.deliveryChallanUrl)}
              iconBackgroundColor="#ede9fe"
              iconColor="#5b21b6"
            />
          )}
          {formData.bomSignedUrl && (
            <AttachmentCard
              href={formData.bomSignedUrl}
              label="BOM (Signed)"
              name={getCleanStorageFileName(formData.bomSignedUrl)}
              iconBackgroundColor="#fee2e2"
              iconColor="#991b1b"
            />
          )}
        </div>
      </div>
      {/* FOOTER TIMELINE */}
      {!loading && formData && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1100px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline orderData={order} dispatchesData={dispatches} dispatchData={formData} />
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const secondaryBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' };

const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500', marginBottom: '10px' };

export default InTransitDetail;
